import { useState, useMemo } from 'react';
import { useDebounce } from './useDebounce';

/**
 * Custom hook for managing list filtering logic
 * Provides common filtering patterns for search, role-based filtering, and sorting
 */
export const useListFilters = (items = [], config = {}) => {
  const {
    // Search configuration
    searchFields = ['name', 'id'],
    searchDebounceMs = 300,
    
    // Role-based filtering configuration
    roleFilter: {
      enabled: roleFilterEnabled = false,
      field: roleFilterField = 'userRoles',
      roleNamePath: roleNamePath = 'role.name'
    } = {},
    
    // Status filtering configuration  
    statusFilter: {
      enabled: statusFilterEnabled = false,
      field: statusFilterField = 'status',
      activeValue: statusActiveValue = 'active',
      inactiveValue: statusInactiveValue = 'inactive',
      initialValue: initialStatusFilter = 'all'
    } = {},
    
    // User-based filtering configuration (for "my items" views)
    userFilter: {
      enabled: userFilterEnabled = false,
      field: userFilterField = 'submittedById',
      currentUserId: currentUserId = null,
      userRoles: currentUserRoles = [],
      submissionRoles: submissionRoles = ['HR', 'Broker', 'CRM'],
      processingRoles: processingRoles = ['Claims_Officer', 'Approver', 'Finance', 'Admin']
    } = {},
    
    // Assignment-based filtering configuration (for claim assignments)
    assignmentFilter: {
      enabled: assignmentFilterEnabled = false,
      field: assignmentFilterField = 'assignedToId',
      currentUserId: assignmentCurrentUserId = null,
      initialValue: initialAssignmentFilter = 'all'
    } = {},
    
    // Sorting configuration
    sorting: {
      defaultSortBy: defaultSortBy = 'createdAt',
      defaultSortOrder: defaultSortOrder = 'desc',
      dateFields: dateFields = ['createdAt', 'updatedAt']
    } = {}
  } = config;

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [assignmentFilter, setAssignmentFilter] = useState(initialAssignmentFilter);
  const [sortBy, setSortBy] = useState(defaultSortBy);
  const [sortOrder, setSortOrder] = useState(defaultSortOrder);

  // Debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, searchDebounceMs);

  // Helper function to get nested object values
  const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    
    return path.split('.').reduce((current, key) => {
      return current?.[key];
    }, obj);
  };

  // Main filtering logic
  const filteredItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    
    let filtered = [...items];

    // Apply user-based filtering (role-based claim filtering)
    if (userFilterEnabled && currentUserRoles && currentUserId) {
      const hasSubmissionOnlyRole = currentUserRoles.some(role => submissionRoles.includes(role));
      const hasProcessingRole = currentUserRoles.some(role => processingRoles.includes(role));

      if (hasSubmissionOnlyRole && !hasProcessingRole) {
        filtered = filtered.filter(item => {
          const itemUserId = getNestedValue(item, userFilterField);
          return itemUserId === currentUserId;
        });
      }
    }

    // Apply search filter
    if (debouncedSearchTerm && searchFields.length > 0) {
      filtered = filtered.filter(item => 
        searchFields.some(field => {
          const value = getNestedValue(item, field);
          if (typeof value === 'string') {
            return value.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
          }
          if (typeof value === 'number') {
            return value.toString().includes(debouncedSearchTerm);
          }
          return false;
        })
      );
    }

    // Apply role filter
    if (roleFilterEnabled && roleFilter) {
      filtered = filtered.filter(item => {
        const userRoles = getNestedValue(item, roleFilterField);
        if (Array.isArray(userRoles)) {
          return userRoles.some(ur => {
            const roleName = getNestedValue(ur, roleNamePath);
            return roleName?.toLowerCase() === roleFilter.toLowerCase();
          });
        }
        return false;
      });
    }

    // Apply status filter
    if (statusFilterEnabled && statusFilter !== 'all') {
      filtered = filtered.filter(item => {
        const itemStatus = getNestedValue(item, statusFilterField);
        if (statusFilter === 'active') {
          return itemStatus === true || itemStatus === statusActiveValue;
        }
        if (statusFilter === 'inactive') {
          return itemStatus === false || itemStatus === statusInactiveValue;
        }
        return itemStatus === statusFilter;
      });
    }

    // Apply assignment filter
    if (assignmentFilterEnabled && assignmentFilter !== 'all') {
      filtered = filtered.filter(item => {
        const assignedToId = getNestedValue(item, assignmentFilterField);
        if (assignmentFilter === 'unassigned') {
          return assignedToId === null || assignedToId === undefined;
        }
        if (assignmentFilter === 'me') {
          return assignedToId === assignmentCurrentUserId;
        }
        return true; // 'all' filter - show all items
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = getNestedValue(a, sortBy);
      let bValue = getNestedValue(b, sortBy);

      // Handle custom field mappings for document management
      if (sortBy === 'uploadDate') {
        aValue = new Date(getNestedValue(a, 'createdAt') || getNestedValue(a, 'updatedAt') || 0);
        bValue = new Date(getNestedValue(b, 'createdAt') || getNestedValue(b, 'updatedAt') || 0);
      } else if (sortBy === 'originalName') {
        aValue = getNestedValue(a, 'name') || '';
        bValue = getNestedValue(b, 'name') || '';
      } else if (sortBy === 'documentType') {
        aValue = getNestedValue(a, 'type') || '';
        bValue = getNestedValue(b, 'type') || '';
      } else if (sortBy === 'fileSize') {
        aValue = getNestedValue(a, 'size') || 0;
        bValue = getNestedValue(b, 'size') || 0;
      }

      // Handle date fields
      if (dateFields.includes(sortBy)) {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }

      // Handle numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle date comparison
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string comparison
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      
      if (sortOrder === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });

    return filtered;
  }, [
    items, 
    debouncedSearchTerm, 
    roleFilter, 
    statusFilter, 
    assignmentFilter,
    sortBy, 
    sortOrder,
    searchFields,
    roleFilterEnabled,
    statusFilterEnabled,
    assignmentFilterEnabled,
    userFilterEnabled,
    currentUserId,
    currentUserRoles,
    submissionRoles,
    processingRoles,
    roleFilterField,
    roleNamePath,
    statusFilterField,
    statusActiveValue,
    statusInactiveValue,
    assignmentFilterField,
    assignmentCurrentUserId,
    userFilterField,
    dateFields
  ]);

  return {
    // Filtered results
    filteredItems,
    
    // Filter state
    searchTerm,
    setSearchTerm,
    roleFilter,
    setRoleFilter,
    statusFilter,
    setStatusFilter,
    assignmentFilter,
    setAssignmentFilter,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    
    // Computed values
    totalItems: items.length,
    filteredCount: filteredItems.length,
    
    // Helper functions
    resetFilters: () => {
      setSearchTerm('');
      setRoleFilter('');
      setStatusFilter('all');
      setAssignmentFilter('all');
      setSortBy(defaultSortBy);
      setSortOrder(defaultSortOrder);
    }
  };
};
